import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Tooltip,
  Typography,
  CircularProgress,
  FormControlLabel,
  FormControl,
  Radio,
  RadioGroup,
  Divider
} from '@mui/material'
import { apiCall, ApiConfig } from '../../../config/api'
import { SnackBar } from '../../SnackBar/Snackbar'
import { ApiResponse } from '../../../models/ApiResponse'
import axios from 'axios'
import HelpIcon from '@mui/icons-material/Help'
import InfoIcon from '@mui/icons-material/Info'
import DownloadIcon from '@mui/icons-material/Download'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import { templateMassiveProcessInventory } from '../../../const/templatesFileName'
import { Selector } from '../../Selector'
import { useWarehouses, useZones } from '../../../hooks'
import { Zone } from '../../../interfaces'

/*
Copyright 2022-07-12 por Alfonso Moya Ecomsur Holding S.A ©
Detalle: Agrega selección warehouse al crear WorkOrder por archivo.
*/

enum eTypeLoad {
  'byWarehouse' = 1,
  'byZone' = 2
}

class SnackBarStatus {
  isOpen: boolean = false
  message: string = ''
  status: string = ''
}

interface UploadOrderFileProps {
  closeModal?: () => void
  onUploadSuccess?: (message: string) => void
}

export const UploadOrderFile: React.FC<UploadOrderFileProps> = ({
  closeModal,
  onUploadSuccess
}) => {
  const {
    zonesState: { zones }
  } = useZones()

  const {
    warehousesState: { warehouses },
    getWarehouses
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    } else if (warehouses?.length === 1) {
      setSelectedWarehouse(warehouses[0].id)
    }
  }, [warehouses])

  const maxFileSize = 2097152
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [selectedFiles, setSelectedFiles] = useState<any[] | undefined>(
    undefined
  )
  const [currentFile, setCurrentFile] = useState<any | undefined>(undefined)
  const [progress, setProgress] = useState<number>(0)
  const [snackBarStatus, setSnackBarStatus] = useState<SnackBarStatus>(
    new SnackBarStatus()
  )
  const [disabledLink, setDisabledLink] = useState<boolean>(false)

  const [selectedWarehouse, setSelectedWarehouse] = useState<number>(-1)
  const [zonesByWarehouse, setZonesByWarehouse] = useState<Zone[]>([])
  const [selectedZone, setSelectedZone] = useState<number>(-1)
  const [showZone, setShowZone] = useState<string>('byWarehouse')

  const handleSelectedWarehouseZones = (zones: Zone[]) => {
    if (selectedWarehouse > 0) {
      const initialZones = [...zones!]

      const filteredZones = initialZones.filter(
        (x: Zone) => x.warehouseId === selectedWarehouse
      )

      setZonesByWarehouse(filteredZones)

      if (filteredZones.filter((x) => x.zoneId === selectedZone).length === 0) {
        setSelectedZone(-1)
      }
    } else {
      setSelectedWarehouse(-1)
      setSelectedZone(-1)
      setZonesByWarehouse([])
    }
  }

  useEffect(() => {
    handleSelectedWarehouseZones(zones!)
  }, [selectedWarehouse, showZone])

  const notify = (message: string, status: string) => {
    const newStatus: SnackBarStatus = {
      isOpen: true,
      message: message,
      status: status
    }
    setSnackBarStatus(newStatus)
  }

  const handleSnackbar = (isOpen: boolean) => {
    setSnackBarStatus({ ...snackBarStatus, isOpen: isOpen })
  }

  const handleSelectFile = (event: any) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
    setSnackBarStatus(new SnackBarStatus())
  }

  const sizeLabel = (size: number): string => {
    let label = ''
    if (size / 1024 / 1024 > 1) label = (size / 1024 / 1024).toFixed(2) + ' MB'
    else if (size / 1024 > 1) label = (size / 1024).toFixed(0) + ' KB'
    else label = size.toFixed(2) + ' Bytes'
    return label
  }

  const validateFile = (files: any[] | undefined): string => {
    if (!files || files.length === 0) {
      return 'Ningún archivo seleccionado'
    }

    const selectedFile = files[0]

    if (selectedFile.size > maxFileSize) {
      const maxLabel = sizeLabel(maxFileSize)
      return `El archivo seleccionado supera el tamaño máximo: ${maxLabel}`
    }

    if (selectedFile.name.toLowerCase().indexOf('.xls') < 0) {
      return `El archivo seleccionado no es de tipo Excel (.xls)`
    }

    return ''
  }

  const getTypeLoad = () => {
    if (showZone === eTypeLoad[eTypeLoad.byWarehouse]) {
      return eTypeLoad.byWarehouse
    }

    return eTypeLoad.byZone
  }

  const callUpload = (file: any, onUploadProgress: (event: any) => void) => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('warehouseId', `${selectedWarehouse}`)
    if (showZone === 'byZone') {
      formData.append('zoneId', `${selectedZone}`)
    }
    formData.append('typeLoad', `${getTypeLoad()}`)

    const axiosInstance = axios.create({
      baseURL: ApiConfig.ProcessApi,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return axiosInstance.post(`/InventoryUploadFile`, formData, {
      onUploadProgress
    })
  }

  const handleUpload = () => {
    const validateError = validateFile(selectedFiles)
    if (validateError !== '') {
      setCurrentFile(undefined)
      notify(validateError, 'error')
      return
    }

    if (!selectedFiles) return

    const selectedFile = selectedFiles[0]
    setProgress(0)
    setCurrentFile(selectedFile)
    setIsUploading(true)

    callUpload(selectedFile, (event) => {
      setProgress(Math.round((100 * event.loaded) / event.total))
    })
      .then((response) => {
        const apiResponse = response.data as ApiResponse
        setIsUploading(false)
        setCurrentFile(undefined)
        setSelectedFiles(undefined)
        setProgress(0)

        // Ante un error el modal queda abierto a proposito: el operario corrige el
        // archivo o la seleccion de almacen/zona y reintenta sin rearmar todo.
        if (!apiResponse.success) {
          notify(
            `Error en carga de archivo: ${
              apiResponse.message || 'falla en el servicio.'
            }`,
            'error'
          )
          return
        }

        const successMessage = 'El archivo ha sido cargado para su procesamiento'
        if (onUploadSuccess) {
          onUploadSuccess(successMessage)
          closeModal?.()
        } else {
          notify(successMessage, 'success')
        }
      })
      .catch((error) => {
        setIsUploading(false)
        setProgress(0)
        notify(
          `Error en carga de archivo: ${
            error.response?.data?.message || 'falla en el servicio.'
          }`,
          'error'
        )
        setCurrentFile(undefined)
        setSelectedFiles(undefined)
        setProgress(0)
      })
  }

  const handleDownloadTemplate = () => {
    setDisabledLink(true)
    setTimeout(() => {
      apiCall(
        'GET',
        `${ApiConfig.ProcessApi}/GetTemplateFile/${templateMassiveProcessInventory}`,
        {
          responseType: 'blob'
        }
      )
        .then((response: any) => {
          if (response.status === 200 && response.data !== null) {
            const raw = window.atob(response.data.file)
            const uInt8Array = new Uint8Array(raw.length)
            for (let i = 0; i < raw.length; ++i) {
              uInt8Array[i] = raw.charCodeAt(i)
            }
            const link = document.createElement('a')
            const blob = new Blob([uInt8Array], {
              type: response.data.mimeType
            })
            link.style.display = 'none'
            link.href = URL.createObjectURL(blob)
            link.setAttribute('download', response.data.name)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        })
        .catch((message: any) => {
          console.log('message: ', message)
        })
      setDisabledLink(false)
    }, 1500)
  }

  React.useEffect(() => {
    if (warehouses?.length === 1) {
      setSelectedWarehouse(warehouses[0].id)
    }
  }, [warehouses])

  const handleSetWarehouse = (id: number) => {
    setSelectedWarehouse(id)
  }

  const handleSetZone = (id: number) => {
    setSelectedZone(id)
  }

  const handleSetShowZone = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowZone((event.target as HTMLInputElement).value)
    setSelectedZone(-1)
    setZonesByWarehouse([])
  }

  const validateButtonLoad = () => {
    if (
      showZone === 'byZone' &&
      (selectedWarehouse <= 0 ||
        !selectedFiles ||
        isUploading ||
        selectedZone <= 0)
    ) {
      return true
    } else {
      if (
        showZone === 'byWarehouse' &&
        (selectedWarehouse <= 0 || !selectedFiles || isUploading)
      ) {
        return true
      } else {
        return false
      }
    }
  }

  return (
    <Grid container spacing={1}>
      {/* Columna Izquierda - Información Importante */}
      <Grid item xs={12} md={5}>
        <Box
          sx={{
            backgroundColor: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: '4px',
            padding: '19px 16px',
            textAlign: 'left'
          }}
        >
          <Box display='flex' alignItems='center' gap={0.5} mb={1}>
            <InfoIcon
              sx={{ color: '#c62828', fontSize: '1.4rem', flexShrink: 0 }}
            />
            <Typography
              variant='subtitle1'
              sx={{ fontWeight: 600, color: '#c62828' }}
            >
              IMPORTANTE:
            </Typography>
          </Box>
          <Typography variant='body2' sx={{ color: '#c62828', mt: 1 }}>
            Recuerde que se eliminarán el stock actual de las posiciones
            correspondientes al almacén o la(s) zona(s) seleccionada(s) y será
            reemplazado por la información contenida en el archivo.
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: '#c62828', fontWeight: 550, mt: 1 }}
          >
            ESTA ACCIÓN NO PUEDE DESHACERSE.
          </Typography>
          <Divider sx={{ my: 0.5, borderColor: 'rgba(198, 40, 40, 0.2)' }} />
          <Box
            component='ul'
            sx={{ color: '#c62828', fontSize: '0.87rem', mt: 1, pl: 2, mb: 0, '& b': { fontWeight: 550 } }}
          >
            <li>
              Si existe un <b>error</b> en el
              archivo, toda la carga se <b>invalidará</b>, favor revise el
              estado de ésta en el menú procesos.
            </li>
            <li>
              Esta funcionalidad <b>no</b> aplica para productos con <b>FEFO</b>{' '}
              o <b>Venta en Verde</b>.
            </li>
            <li>
              Sólo se actualizarán posiciones de <b>venta</b> o{' '}
              <b>reposición</b>.
            </li>
            <li>
              Si elige inventario <b>por zona</b>, sólo se cargará el inventario
              a la <b>zona seleccionada</b>, las demás zonas permanecerán con el
              stock actual.
            </li>
            <li>
              Se recomienda que la operación <b>no</b> tenga{' '}
              <b>rutas de picking en proceso o sin concluir</b>.
            </li>
            <li>
              Se recomienda que la operación <b>no</b> tenga{' '}
              <b>Asn(s) en proceso o sin concluir</b>.
            </li>
          </Box>
        </Box>
      </Grid>

      {/* Columna Derecha - Contenido Principal */}
      <Grid item xs={12} md={7} sx={{ pl: 2 }}>
        <Box
          sx={{
            textAlign: 'left',
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '16px',
            backgroundColor: '#ffffff'
          }}
        >
          <Typography variant='subtitle1' gutterBottom sx={{ fontWeight: 600 }}>
            Seleccione la opción y complete los datos
          </Typography>

          {/* Botón Descargar Plantilla */}
          <Box mb={1}>
            <Typography variant='body2' sx={{ mb: 1, color: 'text.secondary' }}>
              Para cargar el archivo, primero descargue la plantilla:
            </Typography>
            {disabledLink ? (
              <Button
                variant='outlined'
                disabled
                startIcon={<CircularProgress size='1rem' />}
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Descargando...
              </Button>
            ) : (
              <Button
                variant='outlined'
                onClick={handleDownloadTemplate}
                startIcon={<DownloadIcon />}
                size='small'
                sx={{ borderRadius: '8px', textTransform: 'none' }}
              >
                Descargar Plantilla
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Opciones de Selección */}
          <Box mb={1}>
            <Typography variant='body2' sx={{ fontWeight: 580, mb: 0 }}>
              Seleccione si desea reemplazar el stock por almacén o por zona
            </Typography>
            <FormControl size='small'>
              <RadioGroup row onChange={handleSetShowZone} value={showZone}>
                <FormControlLabel
                  value='byWarehouse'
                  control={<Radio size='small' />}
                  label={<Typography variant='body2'>Por almacén</Typography>}
                />
                <FormControlLabel
                  value='byZone'
                  control={<Radio size='small' />}
                  label={<Typography variant='body2'>Por zona</Typography>}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          {/* Selectores en una sola línea */}
          <Box display='flex' gap={2} mb={1}>
            {/* Selector de Almacén */}
            <Box sx={{ flex: 1 }}>
              <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                Almacén:
              </Typography>
              <FormControl fullWidth size='small'>
                <Selector
                  labelId='selectedWarehouse'
                  item={
                    warehouses &&
                    warehouses.map((item) => ({
                      value: item.id,
                      name: item.warehouseName
                    }))
                  }
                  defaultLabel='Seleccione almacén'
                  onChange={(e: any) =>
                    handleSetWarehouse(e.target.value as number)
                  }
                  id='warehouseId'
                  name='warehouseId'
                  value={selectedWarehouse}
                  variant='outlined'
                  size='small'
                />
              </FormControl>
            </Box>

            {/* Selector de Zona */}
            {showZone === 'byZone' && (
              <Box sx={{ flex: 1 }}>
                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>
                  Zona:
                </Typography>
                <FormControl fullWidth size='small'>
                  <Selector
                    labelId='selectedZone'
                    item={
                      zonesByWarehouse &&
                      zonesByWarehouse.map(({ zoneId, zoneName }: Zone) => ({
                        value: zoneId,
                        name: zoneName
                      }))
                    }
                    defaultLabel='Seleccione zona'
                    onChange={(e: any) =>
                      handleSetZone(e.target.value as number)
                    }
                    id='zoneId'
                    name='zoneId'
                    value={selectedZone}
                    variant='outlined'
                    size='small'
                  />
                </FormControl>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Selección de Archivo */}
          <Box>
            <Box display='flex' gap={1} alignItems='center' mb={2}>
              <label htmlFor='btn-upload'>
                <input
                  id='btn-upload'
                  name='btn-upload'
                  style={{ display: 'none' }}
                  type='file'
                  onChange={handleSelectFile}
                  accept='.xls,.xlsx'
                />
                <Tooltip
                  title='Seleccionar el archivo Excel a cargar'
                  placement='top'
                >
                  <Button
                    variant='outlined'
                    component='span'
                    size='small'
                    startIcon={<UploadFileIcon />}
                    sx={{ borderRadius: '8px', textTransform: 'none' }}
                  >
                    Seleccionar Archivo
                  </Button>
                </Tooltip>
              </label>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {selectedFiles && selectedFiles.length > 0
                  ? selectedFiles[0].name
                  : 'Ningún archivo seleccionado'}
              </Typography>
            </Box>

            {/* Progreso */}
            {currentFile && (
              <Box
                sx={{ visibility: currentFile ? 'visible' : 'hidden', mb: 2 }}
              >
                <LinearProgress
                  variant='determinate'
                  value={progress}
                  sx={{
                    height: 15,
                    borderRadius: '5px',
                    backgroundColor: '#EEEEEE',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '5px',
                      backgroundColor: '#1a90ff'
                    }
                  }}
                />
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{ textAlign: 'right', mt: 1 }}
                >
                  {`${progress}%`}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Información adicional inferior */}
          <Box display='flex' alignItems='center' gap={1} mt={1}>
            <HelpIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              Oprima el botón SELECCIONAR para abrir el archivo de carga y luego
              el botón CARGAR para enviar el archivo al servidor para su
              procesamiento.
            </Typography>
          </Box>
        </Box>
      </Grid>

      {/* Botones de Acción */}
      <Grid
        item
        xs={12}
        sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 1 }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => closeModal?.()}
          size='small'
          sx={{ borderRadius: '8px', textTransform: 'none' }}
        >
          CERRAR
        </Button>
        <Tooltip
          title='Enviar el archivo seleccionado al servidor para su procesamiento'
          placement='top'
        >
          <Button
            color='primary'
            variant='contained'
            size='small'
            disabled={validateButtonLoad()}
            onClick={handleUpload}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            CARGAR
          </Button>
        </Tooltip>
      </Grid>

      <SnackBar
        open={snackBarStatus.isOpen}
        setOpen={handleSnackbar}
        message={snackBarStatus.message}
        status={snackBarStatus.status}
      />
    </Grid>
  )
}
